import { useState } from "react";
import Modal from "../../dispatch/actions/Modal";
import type { Deal } from "../../../lib/types";
import { markUnableToFly } from "../../../lib/droneActions";
import { useAuth } from "../../../context/AuthContext";

interface Props {
  open: boolean;
  deal: Deal;
  onClose: () => void;
  onCompleted: () => void;
}

const REASONS = [
  "Geofenced airspace",
  "Weather (wind/rain)",
  "Trees blocking angles",
  "Homeowner declined fly-over",
  "Equipment issue",
  "Other",
];

export default function UnableToFlyModal({
  open,
  deal,
  onClose,
  onCompleted,
}: Props) {
  const { member } = useAuth();
  const [reason, setReason] = useState(REASONS[0]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setErr(null);
    const full =
      reason === "Other"
        ? notes.trim()
        : notes.trim()
          ? `${reason} — ${notes.trim()}`
          : reason;
    if (!full) return setErr("Tell us why — at least pick or write a reason.");
    setSubmitting(true);
    try {
      await markUnableToFly(deal, { reason: full }, member?.id ?? null);
      onCompleted();
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Unable to fly"
      subtitle={`${deal.homeowner_name ?? "(no name)"} · ${deal.property_street ?? ""}`}
      onClose={onClose}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-4 py-1.5 rounded bg-amber-600 text-white text-sm disabled:opacity-60"
          >
            {submitting ? "Flagging…" : "Flag for climb"}
          </button>
        </>
      }
    >
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Reason
        </label>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
        >
          {REASONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Notes{" "}
          {reason === "Other" ? (
            <span className="text-red-500">(required)</span>
          ) : (
            <span className="text-slate-400">(optional)</span>
          )}
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
          placeholder={
            reason === "Other" ? "Describe the issue…" : "Anything else to note"
          }
        />
      </div>

      <p className="text-xs text-slate-500">
        Branches the deal to <code className="text-slate-700">needs_climb_inspection</code>.
        Joy gets notified to authorize a physical inspection; Robert/RS picks
        it up from there.
      </p>

      {err && (
        <div className="bg-red-50 text-red-700 text-sm p-2 rounded">{err}</div>
      )}
    </Modal>
  );
}
